import { ClockIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { useCallback, useEffect } from "react";

import Button from "~/components/buttons/Button";
import CardContent from "~/components/Card/components/CardContent";
import CardHeader from "~/components/Card/components/CardHeader";
import CardTitle from "~/components/Card/components/CardTitle";
import CardWrapper from "~/components/Card/components/CardWrapper";
import Hint from "~/components/Hint";
import WordIcon from "~/components/icons/WordIcon";
import {
	getInvoiceStatusDisplayNameById,
	invoiceStatusId,
} from "~/modules/billing/api/invoiceStatus/invoiceStatusId.ts";
import { All_PENDING_INVOICES_QUERY_KEY } from "~/modules/billing/api/pendingInvoice/pendingInvoiceQueries.ts";
import {
	updateTimeTrackingsEditableStatus,
} from "~/modules/billing/api/processedInvoice/processedInvoiceApiDispatchers.ts";
import { INVOICE_QUERY_KEY } from "~/modules/billing/api/processedInvoice/processedInvoiceQueries.ts";
import type { Invoice } from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import { useInvoiceDownload } from "~/modules/billing/hooks/useInvoiceDownload.ts";

type Props = {
	invoiceData: Invoice,
	onSetInvoiceRecipientClick: () => void,
	setIsBusy: (isBusy: boolean) => void,
	setServerErrorMessage: (message: string) => void
};

const CurrentInvoiceToDos = ({ invoiceData, onSetInvoiceRecipientClick, setIsBusy, setServerErrorMessage }: Props) => {
	const queryClient = useQueryClient();
	const { isGenerating, handleDownload } = useInvoiceDownload(invoiceData.id);
	const timeTrackingsAreEditable = invoiceData.timeTrackingsEditable;
	const isProjectInvoice = invoiceData.isProjectInvoice;
	const isDraftStatus = invoiceData.invoiceStatusId === invoiceStatusId.Draft;
	const isWaitingForApprovalStatus = invoiceData.invoiceStatusId === invoiceStatusId.AwaitingApproval;
	const isApprovedStatus = invoiceData.invoiceStatusId === invoiceStatusId.Approved;
	const isSentToClientStatus = invoiceData.invoiceStatusId === invoiceStatusId.SentToClient;
	const invoiceRecipientIsSet = !!invoiceData.recipientInformation;

	useEffect(() => {
		if (isGenerating) {
			setIsBusy(true);
		} else {
			setIsBusy(false);
		}
	}, [isGenerating, setIsBusy]);

	const handleUpdateTimeTrackingsEditableStatusClick = useCallback(async (enabled: boolean) => {
		try {
			setIsBusy(true);

			await updateTimeTrackingsEditableStatus(invoiceData.id, enabled);
			await queryClient.refetchQueries({ queryKey: INVOICE_QUERY_KEY(invoiceData.id) });

			if (!enabled) {
				queryClient.invalidateQueries({ queryKey: All_PENDING_INVOICES_QUERY_KEY });
			}
		} catch (error) {
			console.log(error);
			setServerErrorMessage(t("form:unexpectedError"));
		}
		setIsBusy(false);
	}, [invoiceData.id, queryClient, setIsBusy, setServerErrorMessage]);

	const invoiceRecipientListElement = <li>Rechnungsempfänger <button onClick={onSetInvoiceRecipientClick}
																		 className="text-danger-500 hover:underline">ermitteln
		/
		bestimmen</button> und / oder mit verantwortlicher Person verifizieren.
	</li>;

	return <CardWrapper hasTitle={true}>
		<CardHeader>
			<CardTitle>Aktuelle To-dos</CardTitle>
			{isDraftStatus && <div className="ml-auto space-x-2">
				<Button theme="none"
						size="auto"
						className="p-1"
						onClick={handleDownload}><WordIcon className="size-5" /></Button>
				{isProjectInvoice && <Button theme="danger"
											 onClick={() => handleUpdateTimeTrackingsEditableStatusClick(!timeTrackingsAreEditable)}
											 className="px-1.5 py-1"
											 size="auto">
					<ClockIcon className="size-5" />
				</Button>}
			</div>}
		</CardHeader>
		<CardContent hasTitle={true}>
			{!timeTrackingsAreEditable && <ul className="ml-4 list-outside list-disc text-sm">
				{isDraftStatus && <>
					<li>Rechnungsdokument <button onClick={handleDownload}
												  className="text-danger-500 hover:underline">herunterladen</button>,
						prüfen und verwantwortlicher Person zur Freigabe vorlegen.
					</li>
					{!invoiceRecipientIsSet && invoiceRecipientListElement}
					{isProjectInvoice &&
						<li>Falls noch Änderungen an den zugrundeliegenden Erfassungen notwendig sind, können
							diese <button onClick={() => handleUpdateTimeTrackingsEditableStatusClick(true)}
										  className="text-danger-500 hover:underline">temporär entsperrt
								werden</button>.
						</li>}
					<li>
						Nach Abschluss aller vorherigen Schritte den Status
						auf &quot;{getInvoiceStatusDisplayNameById(invoiceStatusId.AwaitingApproval)}&quot; setzen.
					</li>
				</>}

				{isWaitingForApprovalStatus && <>
					{!invoiceRecipientIsSet && invoiceRecipientListElement}
					<li>Freigabe des Rechnungsdokuments durch die verantwortliche Person abwarten.</li>
					<li>Nach Freigabe den Status
						auf &quot;{getInvoiceStatusDisplayNameById(invoiceStatusId.Approved)}&quot; ändern.
					</li>
					<li>Optional: Namen der verantwortlichen Person in einem Kommentart hinterlegen.</li>
				</>}

				{isApprovedStatus && <>
					{!invoiceRecipientIsSet && invoiceRecipientListElement}
					<li>Abschließend prüfen, ob alle relevanten Informationen mit dem Rechnungsdokument übereinstimmen
						und ggf. hier korrigieren.
					</li>
					<li>Rechnung an Rechnungsempfänger übermitteln.</li>
					<li>Anschließend den Status
						auf &quot;{getInvoiceStatusDisplayNameById(invoiceStatusId.SentToClient)}&quot; ändern.
					</li>
					<li><strong>Bitte beachten: </strong>Das Versanddatum wird bei der Statusänderung automatisch auf den heutigen Tag gesetzt. Sollte das tatsächliche Datum abweichen,
						kann es anschließend in der Zahlungsdetails-Sektion korrigiert werden.
					</li>
				</>}

				{isSentToClientStatus && <>
					<li>Status
						auf &quot;{getInvoiceStatusDisplayNameById(invoiceStatusId.Paid)}&quot; ändern, sobald der Zahlungseingang festgestellt wurde.
					</li>
					<li><strong>Bitte beachten: </strong>Das Zahlungseingangsdatum wird bei der Statusänderung automatisch auf den heutigen Tag gesetzt. Sollte das tatsächliche Datum abweichen,
					kann es anschließend in der Zahlungsdetails-Sektion korrigiert werden.
					</li>
				</>}
			</ul>}
			{timeTrackingsAreEditable && <Hint size="sm"
											   theme="warning"><p>Das Bearbeiten und Erstellen von
				Erfassungen für diese Rechnung wurde temporär aktiviert, um nachträgliche Änderungen zu
				ermöglichen.</p>
				<br />
				<p>Die Rechnungsstellung kann erst fortgesetzt werden, sobald alle Änderungen vorgenommen wurden und
					die Erfassungen wieder gesperrt werden.</p>
				<br />
				<p><strong>Der Rechnungssbetrag wird bei der Sperrung neu berechnet. Bitte überpüfe, ob das
					Rechnungsdokument erneut heruntergeladen werden muss um sicherzustellen, dass es etwaige Änderungen
					erfassten Zeiten und des Rechnungsbetrags beinhaltet.</strong></p>
				<br />
				<Button theme="primary"
						size="sm"
						onClick={() => handleUpdateTimeTrackingsEditableStatusClick(false)}>Erfassungen jetzt erneut
					sperren</Button>
				<p className="h-3" />
			</Hint>}
		</CardContent>
	</CardWrapper>;
};

export default CurrentInvoiceToDos;